import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/data-population.js
import { logger } from '@/utils/logger';
import { privilegesService } from '@/services/permissions/privilegesService';

const log = logger.store;

/**
 * DataPopulationHandler
 * 
 * Manages data population across all application modules:
 * - Self-Assessment: business segments, company questions
 * - Customer Requests: invitations, responses
 * - Supply Chain: suppliers
 * - Carbon Accounting: emissions, activities, spend, distribution, supplier inputs
 * - User Management: user data
 * 
 * Features:
 * - Intelligent data existence checks
 * - Sequential loading for Carbon Accounting modules to maintain data consistency
 * - Population tracking with timestamps to prevent duplicate operations
 * - Comprehensive error handling and recovery
 * - Throttling to prevent excessive API calls (30-second minimum between checks per project)
 * - Special handling for supply chain suppliers to prevent duplicate populate cycles
 */
export class DataPopulationHandler {
  constructor(store) {
    this.store = store;
    this.isStartupComplete = false;
    this.populationInProgress = new Map(); // Track modules being populated with timestamps
    this.populationResults = new Map(); // Track last result of population by module
    this.lastPopulationCheck = 0; // Track when we last checked for missing data
    this.projectCheckTimestamps = new Map(); // Per-project throttling (30 seconds)
    this.carbonAccountingPopulationInProgress = false; // Dedicated flag for Carbon Accounting modules

    // Add longer timeout for startup phase to ensure all init is complete
    setTimeout(() => {
      this.isStartupComplete = true;
    }, 5000); // 5 seconds for better reliability
  }

  /**
   * Mark initialization as complete
   */
  markInitializationComplete() {
    this.isStartupComplete = true;
    // Init complete logging silenced
  }

  /**
   * Check authentication state before population
   * Validates both authentication status and token validity
   */
  isAuthenticatedAndValid() {
    try {
      // Import auth service
      const { authService } = require('@/services/auth');
      const authState = authService.getAuthState();

      // Check if authenticated and has valid token
      if (!authState?.authenticated || !authState.token) {
        return false;
      }

      // Check if token is still valid using the new validation method
      if (authService.isTokenValid && !authService.isTokenValid(authState.token)) {
        log.log('Token is expired, skipping data population');
        return false;
      }

      return true;
    } catch (error) {
      log.warn('Error checking auth state:', error);
      return false;
    }
  }

  /**
   * Check for missing data and populate if needed
   * Uses sequential loading for Carbon Accounting modules to maintain data consistency
   * @param {string|number} projectId The project ID
   * @param {Object} options Additional options
   * @returns {Promise<void>}
   */
  async checkAndPopulateMissingData(projectId, options = {}) {
    // Skip if no projectId
    if (!projectId) {
      log.warn('No project ID provided for data population');
      return;
    }

    // Check authentication before proceeding
    if (!this.isAuthenticatedAndValid()) {
      log.log('Not authenticated or token invalid, skipping data population');
      return;
    }

    // Skip during early startup phase to avoid conflicts
    if (!this.isStartupComplete && !options.force) {
      log.log('Deferring population check until startup is complete');
      return;
    }

    // Per-project throttling (30 seconds per project)
    const now = Date.now();
    const projectKey = `project_${projectId}`;
    const lastProjectCheck = this.projectCheckTimestamps.get(projectKey) || 0;
    const timeSinceProjectCheck = now - lastProjectCheck;

    // Only allow checks every 30 seconds per project unless force=true
    if (timeSinceProjectCheck < 30000 && !options.force) {
      return;
    }

    // Update timestamp for this project
    this.projectCheckTimestamps.set(projectKey, now);

    // ✅ Operation Lightsaber Part 2 FIX: Check each module independently
    // The previous "isFirstLogin" approach was flawed - it would skip ALL population
    // if ANY module had data. Now we check each module and only populate those that are empty.
    // This ensures customerRequests gets populated even if businessSegments has data, etc.

    // Separate Carbon Accounting modules from other modules
    const carbonModules = [
      'emissionResults',
      'activityData',
      'spendVolume',
      'spendDistribution',
      'supplierInput'
    ];

    const otherModules = [
      'customerRequests',
      'businessSegments',
      'companyQuestions',
      'supplychainSuppliers',
      'users'
    ];

    // Check if any Carbon Accounting modules need population
    const carbonModulesNeedPopulation = carbonModules.some(moduleName =>
      !this.hasDataInStore(moduleName)
    );

    // Gather other modules that need population (check BEFORE starting any population)
    const modulesToPopulate = [];
    for (const moduleName of otherModules) {
      try {
        // Skip if population is already in progress for this module
        if (this.populationInProgress.has(moduleName)) {
          const populationStartTime = this.populationInProgress.get(moduleName);
          const timeElapsed = now - populationStartTime;

          // If population has been running for over 30 seconds, consider it stalled
          if (timeElapsed > 30000) {
            log.warn(`Module ${moduleName} population appears stalled (${timeElapsed}ms), will retry`);
            this.populationInProgress.delete(moduleName);
          } else {
            continue;
          }
        }

        // Check last population result - if recent success, skip
        const lastResult = this.populationResults.get(moduleName);
        if (lastResult && lastResult.timestamp && !options.force) {
          const timeSinceLastPopulation = now - lastResult.timestamp;
          if (lastResult.success && timeSinceLastPopulation < 5 * 60 * 1000) continue;
          if (!lastResult.success && timeSinceLastPopulation < 30 * 1000) continue;
        }

        // Check if module has data
        if (!this.hasDataInStore(moduleName)) {
          modulesToPopulate.push(this.getModuleByName(moduleName));
        }
      } catch (error) {
        log.error(`Error checking ${moduleName}:`, error);
      }
    }

    // If nothing needs population, exit early
    if (!carbonModulesNeedPopulation && modulesToPopulate.length === 0) {
      return;
    }

    // Helper to populate other modules in batches
    const populateOtherModules = async () => {
      if (modulesToPopulate.length === 0) return [];

      // Sort modules by priority (lower number = higher priority)
      modulesToPopulate.sort((a, b) => a.priority - b.priority);

      const allResults = [];
      const batchSize = 3; // Increased from 2 to 3 for faster parallel processing

      for (let i = 0; i < modulesToPopulate.length; i += batchSize) {
        const batch = modulesToPopulate.slice(i, i + batchSize);

        const promises = batch.map(async (module) => {
          this.populationInProgress.set(module.name, Date.now());

          try {
            const startTime = Date.now();
            await this.populateModule(projectId, module);
            const populationTime = Date.now() - startTime;

            // Supply chain suppliers: mark stale instead of reload to prevent race
            if (module.name !== 'supplychainSuppliers') {
              await this.reloadModuleData(module);
            } else {
              if (this.store.supplychainSuppliersHandler) {
                this.store.supplychainSuppliersHandler.markStale('Population completed');
              }
            }

            this.populationResults.set(module.name, {
              success: true,
              timestamp: Date.now(),
              duration: populationTime
            });

            return { module: module.name, success: true };
          } catch (error) {
            log.error(`Error populating ${module.name}:`, error);
            this.populationResults.set(module.name, {
              success: false,
              timestamp: Date.now(),
              error: error.message
            });
            return { module: module.name, success: false, error: error.message };
          } finally {
            this.populationInProgress.delete(module.name);
          }
        });

        const batchResults = await Promise.allSettled(promises);
        batchResults.forEach(result => {
          if (result.status === 'fulfilled') {
            allResults.push(result.value);
          }
        });

        // Small delay between batches to prevent API rate limits
        if (i + batchSize < modulesToPopulate.length) {
          await new Promise(resolve => setTimeout(resolve, 500)); // Reduced from 1000ms
        }
      }

      return allResults;
    };

    // ✅ Operation Lightsaber V2: Run CA and other modules CONCURRENTLY
    // Previously CA ran first, then other modules started after CA completed.
    // Now both run in parallel for significant time savings on first login.
    const populationTasks = [];

    if (carbonModulesNeedPopulation) {
      populationTasks.push(
        this.loadCarbonAccountingModulesSequentially(projectId)
          .then(result => ({ type: 'carbon', result }))
      );
    }

    if (modulesToPopulate.length > 0) {
      populationTasks.push(
        populateOtherModules()
          .then(result => ({ type: 'other', result }))
      );
    }

    // Wait for both to complete
    await Promise.allSettled(populationTasks);
  }

  /**
   * Load Carbon Accounting modules with optimized parallelization
   * Strategy: 4 modules parallel, then supplierInput (depends on spendDistribution)
   * @param {string|number} projectId Project ID
   * @returns {Promise<Object>} Result of the loading operation
   */
  async loadCarbonAccountingModulesSequentially(projectId) {
    // Skip for lead firms - they use dedicated item+s API endpoints
    if (privilegesService.isLeadFirm()) {
      log.log('Skipping traditional CA modules population for lead firm (uses item+s API)');
      return { success: true, reason: 'leadfirm-uses-items-api', completedModules: [] };
    }

    // Skip if already in progress to prevent duplicate calls
    if (this.carbonAccountingPopulationInProgress) {
      log.log('Carbon Accounting modules population already in progress, skipping');
      return { success: false, reason: 'already-in-progress' };
    }

    if (!projectId) {
      log.warn('Cannot load Carbon Accounting modules: No project ID available');
      return { success: false, reason: 'missing-project-id' };
    }

    // Check auth before starting
    if (!this.isAuthenticatedAndValid()) {
      log.log('Not authenticated, skipping Carbon Accounting population');
      return { success: false, reason: 'not-authenticated' };
    }

    this.carbonAccountingPopulationInProgress = true;

    try {
      // Check auth before starting
      if (!this.isAuthenticatedAndValid()) {
        return { success: false, reason: 'not-authenticated' };
      }

      const results = [];
      const startTime = Date.now();
      const completedModules = [];

      // Helper to populate and reload a single module
      const processModule = async (moduleName, handlerMethod) => {
        // Check if data already exists
        if (this.hasDataInStore(moduleName)) {
          return { module: moduleName, action: 'skipped', hasData: true };
        }

        // Check if already in progress
        if (this.populationInProgress.has(moduleName)) {
          const elapsed = Date.now() - this.populationInProgress.get(moduleName);
          if (elapsed < 30000) {
            return { module: moduleName, action: 'in_progress', timeElapsed: elapsed };
          }
          this.populationInProgress.delete(moduleName);
        }

        this.populationInProgress.set(moduleName, Date.now());

        try {
          // Populate via API
          const moduleObj = this.getModuleByName(moduleName);
          if (!moduleObj) throw new Error(`Module config not found: ${moduleName}`);

          const populationSuccess = await this.populateModule(projectId, moduleObj);

          // Reload via handler
          let reloadSuccess = false;
          const [handlerName, methodName] = handlerMethod.split('.');

          if (this.store[handlerName]?.[methodName]) {
            try {
              await Promise.race([
                this.store[handlerName][methodName](true),
                new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
              ]);
              reloadSuccess = true;
            } catch {
              reloadSuccess = false;
            }
          }

          this.populationResults.set(moduleName, { success: populationSuccess && reloadSuccess, timestamp: Date.now() });
          completedModules.push(moduleName);

          return { module: moduleName, action: 'populated', population: populationSuccess ? 'success' : 'failed', reload: reloadSuccess ? 'success' : 'failed' };
        } catch (error) {
          this.populationResults.set(moduleName, { success: false, timestamp: Date.now(), error: error.message });
          return { module: moduleName, action: 'error', error: error.message };
        } finally {
          this.populationInProgress.delete(moduleName);
        }
      };

      // PHASE 1: Load 4 independent modules in PARALLEL
      const parallelModules = [
        { name: 'emissionResults', handler: 'emissionsHandler.loadAllEmissionResults' },
        { name: 'activityData', handler: 'activitiesHandler.loadActivityData' },
        { name: 'spendVolume', handler: 'spendHandler.loadSpendVolume' },
        { name: 'spendDistribution', handler: 'spendDistributionHandler.loadSpendDistribution' }
      ];

      const parallelResults = await Promise.allSettled(
        parallelModules.map(m => processModule(m.name, m.handler))
      );

      parallelResults.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          results.push(result.value);
        } else {
          results.push({ module: parallelModules[i].name, action: 'error', error: result.reason?.message });
        }
      });

      // PHASE 2: Load supplierInput AFTER spendDistribution (has dependency)
      const supplierInputResult = await processModule('supplierInput', 'supplierInputHandler.loadSupplierInput');
      results.push(supplierInputResult);

      const totalTime = Date.now() - startTime;

      return {
        success: results.some(r => r.action === 'populated' && r.population === 'success'),
        results,
        completedModules,
        duration: totalTime
      };
    } finally {
      this.carbonAccountingPopulationInProgress = false;
    }
  }

  /**
   * Check if module has data in the store
   * Checks for basic existence of data for a project ID
   * @param {string} moduleName Module name
   * @returns {boolean} True if data exists
   */
  hasDataInStore(moduleName) {
    const state = this.store.state;

    // SPECIAL CASE: Check if these specific modules are still loading
    if (state.loading[moduleName]) {
      log.log(`Module ${moduleName} is still loading, skipping population check`);
      return true; // Pretend we have data to prevent unnecessary population
    }

    // CARBON ACCOUNTING MODULES - Simplified existence checks
    switch (moduleName) {
      case 'emissionResults': {
        // Check if we have any emission results with actual data
        const emissionYears = Object.keys(state.data.emissionResults?.byYear || {});
        if (emissionYears.length === 0) return false;

        // Check if at least one year has actual data
        return emissionYears.some(year =>
          Array.isArray(state.data.emissionResults.byYear[year]) &&
          state.data.emissionResults.byYear[year].length > 0
        );
      }

      case 'activityData': {
        // Check if we have any activity data with actual items
        const activityYears = Object.keys(state.data.activityData?.byYear || {});
        if (activityYears.length === 0) return false;

        // Now check if at least one year has actual data in any scope
        return activityYears.some(year => {
          const yearData = state.data.activityData.byYear[year];
          return (Array.isArray(yearData.scope1) && yearData.scope1.length > 0) ||
            (Array.isArray(yearData.scope2) && yearData.scope2.length > 0) ||
            (Array.isArray(yearData.scope3) && yearData.scope3.length > 0);
        });
      }

      case 'spendVolume': {
        // Check if we have any spend volume data with actual values
        const spendYears = Object.keys(state.data.spendVolume?.byYear || {});
        if (spendYears.length === 0) return false;

        // Check if at least one year has actual data
        return spendYears.some(year => {
          const yearData = state.data.spendVolume.byYear[year];
          // Spend volume can be an object with properties or have direct values
          return yearData && (
            // Either it has specific fields we care about
            (yearData.turnoverInThsdUSD !== undefined || yearData.spendShareOfTurnover !== undefined) ||
            // Or it has any properties at all
            Object.keys(yearData).length > 0
          );
        });
      }

      case 'spendDistribution': {
        // Check if we have any spend distribution with actual items
        const distributionYears = Object.keys(state.data.spendDistribution?.byYear || {});
        if (distributionYears.length === 0) return false;

        // Check if at least one year has actual distribution data
        return distributionYears.some(year =>
          Array.isArray(state.data.spendDistribution.byYear[year]) &&
          state.data.spendDistribution.byYear[year].length > 0
        );
      }

      case 'supplierInput': {
        // Check if we have any supplier input with actual items
        const supplierYears = Object.keys(state.data.supplierInput?.byYear || {});
        if (supplierYears.length === 0) return false;

        // Check if at least one year has actual supplier data
        return supplierYears.some(year =>
          Array.isArray(state.data.supplierInput.byYear[year]) &&
          state.data.supplierInput.byYear[year].length > 0
        );
      }

      // NON-CARBON ACCOUNTING MODULES - Keep existing checks
      case 'customerRequests':
        return (state.data.customerRequests?.requests || []).length > 0;

      case 'businessSegments':
        return Object.keys(state.data.businessSegments?.bySector || {}).length > 0 &&
          Object.values(state.data.businessSegments?.bySector || {}).some(arr => arr.length > 0);

      case 'companyQuestions':
        return !!(state.data.companyQuestions?.data?.questionnaire?.length);

      case 'supplychainSuppliers':
        // ✅ FIX: Check if data was fetched, not if list has items
        // This correctly treats "0 suppliers" as valid data (not missing data)
        return this.store.supplychainSuppliersHandler?.lastFetchTime > 0;

      case 'users':
        return (state.data.users?.list || []).length > 0;

      default:
        return false;
    }
  }

  /**
   * Populate a specific module
   * Includes comprehensive error handling, timeout protection, and API path resolution.
   * Timeout set to 5 minutes to accommodate large datasets.
   * @param {string|number} projectId Project ID
   * @param {Object} module Module configuration
   * @returns {Promise<boolean>} Success status
   */
  async populateModule(projectId, module) {
    try {
      // Module population logging silenced

      // Check auth before making API call
      if (!this.isAuthenticatedAndValid()) {
        throw new Error('Not authenticated or token invalid');
      }

      // Ensure module has apiPath
      const apiPath = module.apiPath || this.getModuleApiPath(module.name);
      if (!apiPath) {
        log.error(`Cannot populate module ${module.name}: no API path available`);
        return false;
      }

      // Build the API URL
      const strapiUrl = process.env.GATSBY_STRAPI_URL || '';
      const url = `${strapiUrl}/api/${apiPath}/project/${projectId}/populate`;

      // Authorization header if available
      const headers = {
        'Content-Type': 'application/json'
      };

      // Get valid token from auth service
      const { authService } = require('@/services/auth');
      const authState = authService.getAuthState();

      if (authState?.token && authService.isTokenValid && authService.isTokenValid(authState.token)) {
        headers['Authorization'] = `Bearer ${authState.token}`;
      } else if (getStrapiToken()) {
        headers['Authorization'] = `Bearer ${getStrapiToken()}`;
      } else {
        throw new Error('No valid authentication token available');
      }

      // Prepare request body with module-specific parameters
      const body = this.getModulePopulationParams(module);

      // Population endpoint logging silenced

      // Add timeout to prevent hanging requests
      // Increased timeout from 15 seconds to 5 minutes (300000ms) for large dataset users
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

      try {
        // Call population endpoint
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Enhanced error handling
        if (!response.ok) {
          // Try to get more detailed error message
          let errorDetail = '';
          try {
            const errorJson = await response.json();
            errorDetail = errorJson.error?.message || JSON.stringify(errorJson);
          } catch (e) {
            errorDetail = `Status ${response.status} ${response.statusText}`;
          }

          log.warn(`Population endpoint returned status ${response.status} for ${module.name}: ${errorDetail}`);

          // For 404 errors, try fallback API path
          if (response.status === 404 && module.fallbackApiPath) {
            return this.populateModuleWithFallback(projectId, module);
          }

          // For auth errors, don't retry
          if (response.status === 401 || response.status === 403) {
            throw new Error(`Authentication failed for ${module.name}: ${errorDetail}`);
          }

          throw new Error(`Failed to populate ${module.name}: ${errorDetail}`);
        }

        // HTTP 200 alone is not success: the populate endpoints return
        // success: false in the body for no-ops (population lock held,
        // upstream fetch failed). Treating those as success is what let
        // repopulation flags get cleared without any repopulation happening.
        const responseBody = await response.json().catch(() => null);
        if (responseBody && responseBody.success === false) {
          log.warn(`Population endpoint reported failure for ${module.name}:`, responseBody.message || responseBody.reason || 'no reason given');
          return false;
        }

        return true;
      } catch (fetchError) {
        clearTimeout(timeoutId);

        // Handle abort error specifically
        if (fetchError.name === 'AbortError') {
          log.error(`Population request for ${module.name} timed out after 5 minutes`);
          throw new Error(`Timeout while populating ${module.name}`);
        }

        // Better error classification
        if (fetchError instanceof TypeError && fetchError.message.includes('NetworkError')) {
          throw new Error(`Network error while populating ${module.name}: Check your connection`);
        }

        throw fetchError;
      }
    } catch (error) {
      log.error(`Error populating ${module.name}:`, error);
      throw error;
    }
  }

  /**
   * Try to populate a module using fallback API path
   * @param {string|number} projectId Project ID
   * @param {Object} module Module configuration
   * @returns {Promise<boolean>} Success status
   */
  async populateModuleWithFallback(projectId, module) {
    if (!module.fallbackApiPath) return false;

    log.log(`Trying fallback API path for ${module.name}: ${module.fallbackApiPath}`);

    // Create modified module with fallback path
    const fallbackModule = {
      ...module,
      apiPath: module.fallbackApiPath,
      fallbackApiPath: null // Prevent infinite recursion
    };

    // Try population with fallback path
    return this.populateModule(projectId, fallbackModule);
  }

  /**
   * Reload module data using its handler
   * Better error handling and retries
   * @param {Object} module Module configuration
   * @returns {Promise<boolean>} Success status
   */
  async reloadModuleData(module) {
    try {
      if (!module.handlerMethod) {
        log.warn(`No handler method defined for ${module.name}`);
        return false;
      }

      const [handlerName, methodName] = module.handlerMethod.split('.');

      if (this.store[handlerName] && typeof this.store[handlerName][methodName] === 'function') {
        log.log(`Reloading data for ${module.name} using ${module.handlerMethod}`);

        // Add timeout to prevent hanging
        // For businessSegments, force=true triggers a POST populate — wasteful since
        // populateModule() already populated. Just GET the fresh data.
        // For other modules, true=forceRefresh/cache-bust which is safe.
        const arg = module.name === 'businessSegments' ? false : true;
        const reloadPromise = this.store[handlerName][methodName](arg);

        // Wait for reload with 10 second timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Reload timeout for ${module.name}`)), 10000);
        });

        await Promise.race([reloadPromise, timeoutPromise]);
        return true;
      } else {
        log.warn(`Handler method ${module.handlerMethod} not found`);
        return false;
      }
    } catch (error) {
      log.error(`Error reloading ${module.name}:`, error);
      return false;
    }
  }

  /**
   * Repopulate Carbon Accounting modules in the correct order
   * @param {boolean} reloadAfterPopulate Whether to reload data after population
   * @returns {Promise<Object>} Population results
   */
  async repopulateCarbonAccountingModules() {
    const projectId = this.getProjectId();
    if (!projectId) {
      log.warn('No project ID available for repopulation');
      return { success: false, reason: 'missing-project-id' };
    }

    // CA repopulation logging silenced

    // Load Carbon Accounting modules sequentially
    const result = await this.loadCarbonAccountingModulesSequentially(projectId);

    return {
      success: result.success,
      message: `Repopulated ${result.completedModules.length} Carbon Accounting modules`,
      details: result
    };
  }

  /**
   * Repopulate specific data types after important actions
   * Better handling of carbon accounting modules, improved reporting
   * @param {string[]} dataTypes Data types to repopulate
   * @param {boolean} reloadAfterPopulate Whether to reload data after population
   * @param {Object} options Additional options
   * @returns {Promise<Object>} Population results
   */
  async repopulateAfterAction(dataTypes = [], reloadAfterPopulate = true, options = {}) {
    const projectId = this.getProjectId();
    if (!projectId) {
      log.warn('No project ID available for repopulation');
      return { success: false, reason: 'missing-project-id' };
    }

    // Check auth before repopulating
    if (!this.isAuthenticatedAndValid()) {
      log.log('Not authenticated, skipping repopulation');
      return { success: false, reason: 'not-authenticated' };
    }

    // Identify Carbon Accounting modules
    const carbonAccountingModules = ['emissionResults', 'activityData', 'spendVolume', 'spendDistribution', 'supplierInput'];

    // Skip carbon accounting modules during WebSocket connection refresh
    // unless specifically requested to include them
    if ((options.source === 'websocket' || options.source === 'connection') &&
      !options.includeCarbonAccounting) {
      dataTypes = dataTypes.filter(type => !carbonAccountingModules.includes(type));

      if (dataTypes.length === 0) {
        log.log('No non-CA modules to repopulate from websocket connection, skipping');
        return { success: true, reason: 'no-modules-from-websocket' };
      }
    }

    // Track results
    const results = [];

    // Check if any Carbon Accounting modules are in the list
    const hasCarbonAccountingModules = dataTypes.some(type => carbonAccountingModules.includes(type));

    // If any Carbon Accounting module is requested, load all in sequence
    if (hasCarbonAccountingModules) {
      log.log('Carbon Accounting modules detected, loading in sequence');

      const requestedCaModules = dataTypes.filter(type => carbonAccountingModules.includes(type));

      // Load all Carbon Accounting modules in sequence to maintain data consistency
      await this.loadCarbonAccountingModulesSequentially(projectId);

      // CA modules load as one sequential unit; record them in results so
      // callers gating on per-module success (repopulation flag clearing)
      // see every requested type accounted for.
      requestedCaModules.forEach(type => results.push({ dataType: type, success: true }));

      // Remove all Carbon Accounting modules from individual repopulation
      dataTypes = dataTypes.filter(type => !carbonAccountingModules.includes(type));
    }

    if (dataTypes.length === 0) {
      return {
        success: true,
        results,
        hasCarbonAccountingModules
      };
    }

    // Repopulation logging silenced

    const startTime = Date.now();

    // Process each data type
    for (const dataType of dataTypes) {
      const module = this.getModuleByName(dataType);
      if (!module) {
        results.push({ dataType, success: false, reason: 'unknown-data-type' });
        continue;
      }

      // Skip if population is already in progress for this module
      if (this.populationInProgress.has(module.name)) {
        results.push({ dataType, success: false, reason: 'already-in-progress' });
        continue;
      }

      this.populationInProgress.set(module.name, Date.now());

      try {
        // Call population API
        const success = await this.populateModule(projectId, module);
        results.push({ dataType, success });

        // Record result
        this.populationResults.set(module.name, {
          success,
          timestamp: Date.now()
        });
      } catch (error) {
        log.error(`Error repopulating ${dataType}:`, error);
        results.push({ dataType, success: false, error: error.message });

        // Record failure
        this.populationResults.set(module.name, {
          success: false,
          timestamp: Date.now(),
          error: error.message
        });
      } finally {
        this.populationInProgress.delete(module.name);
      }
    }

    // Reload data in handlers if requested
    if (reloadAfterPopulate) {
      for (const dataType of dataTypes) {
        const module = this.getModuleByName(dataType);
        if (module) {
          // Supply chain suppliers require special handling to prevent duplicate population operations.
          // After repopulate completes, the backend database contains fresh data. Immediately calling
          // reloadModuleData would trigger a GET request that can race with the populate operation,
          // causing the backend to detect an empty state and initiate another populate cycle.
          // Instead, we mark the data as stale and defer loading until the user navigates to the page.
          if (module.name !== 'supplychainSuppliers') {
            await this.reloadModuleData(module);
          } else {
            log.log(`Skipping reload for ${module.name} - data already fresh from populate`);
            if (this.store.supplychainSuppliersHandler) {
              this.store.supplychainSuppliersHandler.markStale('Repopulation completed');
            }
          }
        }
      }
    }

    const totalTime = Date.now() - startTime;
    // Repopulation completion logging silenced

    return {
      success: results.some(r => r.success) || hasCarbonAccountingModules,
      results,
      hasCarbonAccountingModules,
      duration: totalTime
    };
  }

  /**
   * Get project ID from store
   * @returns {string|null} Project ID
   */
  getProjectId() {
    return this.store.getProjectId();
  }

  /**
   * Get API path for a module name using standard conventions
   * @param {string} moduleName Module name
   * @returns {string} API path
   */
  getModuleApiPath(moduleName) {
    const apiPathMap = {
      'customerRequests': 'customer-requests',
      'businessSegments': 'business-segments',
      'companyQuestions': 'company-questions',
      'supplychainSuppliers': 'supplychain-suppliers',
      'users': 'user-management/project',
      'emissionResults': 'ca-emission-results',
      'activityData': 'ca-activities-data',
      'spendVolume': 'ca-spend-volumes',
      'spendDistribution': 'ca-spend-distributions',
      'supplierInput': 'ca-supplier-inputs'
    };

    return apiPathMap[moduleName] || moduleName.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  /**
   * Get module-specific population parameters
   * More comprehensive parameters for each module type
   * @param {Object} module Module configuration
   * @returns {Object} Parameters for the module's population endpoint
   */
  getModulePopulationParams(module) {
    // Base parameter object
    const params = {
      // Always include force: true for reliable population
      force: true
    };

    // Special case for suppliers module
    if (module.name === 'supplychainSuppliers') {
      return {
        ...params,
        force: true,
        immediate: true,
        critical: true,
        // Include both parameter formats to ensure it works
        requesterProjectId: this.store.getProjectId(),
        projectId: this.store.getProjectId()
      };
    }

    // Apply module-specific parameters if specified
    if (module.params) {
      Object.keys(module.params).forEach(key => {
        if (module.params[key] === true) {
          params[key] = this.store.getProjectId();
        } else {
          params[key] = module.params[key];
        }
      });
    }

    switch (module.name) {
      case 'businessSegments':
        // Business segments need force: true to ensure population
        return { ...params, force: true };

      case 'users':
        // Users module might need special parameters
        return { ...params, immediate: true };

      case 'emissionResults':
      case 'activityData':
      case 'spendVolume':
      case 'spendDistribution':
      case 'supplierInput':
        // Carbon accounting modules might need special parameters
        return { ...params, critical: true, immediate: true };

      default:
        return params;
    }
  }

  /**
   * Get all modules configuration
   * More reliable module configuration, added fallback options
   * @returns {Array} Array of module configurations
   */
  getAllModules() {
    return [
      // Self-Assessment
      {
        name: 'customerRequests',
        apiPath: 'customer-requests',
        urlParam: 'projectId',
        handlerMethod: 'customerRequestsHandler.loadCustomerRequests',
        priority: 1
      },
      {
        name: 'businessSegments',
        apiPath: 'business-segments',
        urlParam: 'projectId',
        handlerMethod: 'businessSegmentsHandler.loadBusinessSegments',
        priority: 2
      },
      {
        name: 'companyQuestions',
        apiPath: 'company-questions',
        urlParam: 'projectId',
        handlerMethod: 'companyQuestionsHandler.loadCompanyQuestions',
        priority: 3
      },

      // Supply Chain
      {
        name: 'supplychainSuppliers',
        apiPath: 'supplychain-suppliers',
        urlParam: 'projectId', // Changed from 'requesterProjectId' to match actual endpoint
        handlerMethod: 'supplychainSuppliersHandler.loadSupplychainSuppliers',
        priority: 4,
        // Add fallback API paths to try
        fallbackApiPath: 'supplychain-suppliers/requester',
        // Special parameters for this module
        params: {
          requesterProjectId: true, // Also include this parameter
          immediate: true,
          critical: true
        }
      },

      // User Management
      {
        name: 'users',
        apiPath: 'user-management/project',
        urlParam: 'projectId',
        handlerMethod: 'usersHandler.loadUsers',
        priority: 5
      },

      // Carbon Accounting - Note the specific ordering
      {
        name: 'emissionResults',
        apiPath: 'ca-emission-results',
        urlParam: 'projectId',
        handlerMethod: 'emissionsHandler.loadAllEmissionResults',
        priority: 6
      },
      {
        name: 'activityData',
        apiPath: 'ca-activities-data',
        urlParam: 'projectId',
        handlerMethod: 'activitiesHandler.loadActivityData',
        priority: 7
      },
      {
        name: 'spendVolume',
        apiPath: 'ca-spend-volumes',
        urlParam: 'projectId',
        handlerMethod: 'spendHandler.loadSpendVolume',
        priority: 8
      },
      {
        name: 'spendDistribution',
        apiPath: 'ca-spend-distributions',
        urlParam: 'projectId',
        handlerMethod: 'spendDistributionHandler.loadSpendDistribution',
        priority: 9
      },
      {
        name: 'supplierInput',
        apiPath: 'ca-supplier-inputs',
        urlParam: 'projectId',
        handlerMethod: 'supplierInputHandler.loadSupplierInput',
        priority: 10
      }
    ];
  }

  /**
   * Get a module configuration by name
   * @param {string} name Module name
   * @returns {Object|null} Module configuration
   */
  getModuleByName(name) {
    return this.getAllModules().find(m => m.name === name) || null;
  }
}